import { ReactNode } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { BuilderConfigTab } from 'molecules/kpiBuilder/BuilderConfigTab';
import { BuilderDataTab } from 'molecules/kpiBuilder/BuilderDataTab';
import { BuilderFiltersTab } from 'molecules/kpiBuilder/BuilderFiltersTab';
import { BuilderTabContent } from 'molecules/kpiBuilder/BuilderTabContent';
import { TabId } from 'helpers/kpiBuilder/types';
import { TAB_DISPLAY_ORDER } from 'constants/appConstants';
import { Info } from 'components/layouts/config-view/config-detail/visualisations/Info';
import { Data } from 'components/layouts/config-view/config-detail/visualisations/Data';
import { Config } from 'components/layouts/config-view/config-detail/visualisations/Config';
import { Filters } from 'components/layouts/config-view/config-detail/visualisations/Filters';
import { Access } from 'components/layouts/config-view/config-detail/visualisations/Access';
import { Dashboards } from 'components/layouts/config-view/config-detail/visualisations/Dashboards';
import { IFormValues } from 'helpers/visualizationEditor/types';

type FormApi = Pick<UseFormReturn<IFormValues>, 'register' | 'setValue' | 'formState' | 'watch'>;

interface TabItemsArgs {
  form: FormApi;
  /** Renders a tab's name, with its error or warning dot. */
  tabLabel: (name: string, idx: number) => ReactNode;
  builderMode: boolean;
  /** Keep the JSON editors' generated values instead of starter templates. */
  keepGeneratedFields: boolean;
  isExisting: boolean;
  invalidQuery: boolean;
  saveAttempted: boolean;
  chartType: string;
}

/** The six editor tabs. In builder mode Data, Filters and Additional Config show the builder's questions instead of JSON. */
export function buildVisualizationTabItems(a: TabItemsArgs) {
  const { register, setValue, formState, watch } = a.form;
  const withIssues = (tab: TabId, content: JSX.Element) => (
    <BuilderTabContent tab={tab} builderMode={a.builderMode}>{content}</BuilderTabContent>
  );
  const item = (id: number, name: string, slug: string, content: JSX.Element) => ({
    id, name: a.tabLabel(name, id), target: `#nav-${slug}`, control: `nav-${slug}`, tabId: `nav-${slug}-tab`, content,
  });

  const tabs = [
    item(0, 'Info', 'info', withIssues('info', <Info register={register} setValue={setValue} formState={formState} isExisting={a.isExisting} chartType={a.chartType} builderMode={a.builderMode} />)),
    item(1, 'Data', 'data', withIssues('data', a.builderMode ? <BuilderDataTab /> : (
      <Data register={register} setValue={setValue} formState={formState} invalidQuery={a.invalidQuery} isExisting={a.keepGeneratedFields} chartType={a.chartType} />
    ))),
    item(2, 'Filters', 'filters', withIssues('filters', a.builderMode ? <BuilderFiltersTab /> : (
      <Filters register={register} setValue={setValue} formState={formState} isExisting={a.keepGeneratedFields} />
    ))),
    item(3, 'Additional Config', 'config', withIssues('display', a.builderMode ? <BuilderConfigTab /> : (
      <Config register={register} setValue={setValue} formState={formState} watch={watch} isExisting={a.keepGeneratedFields} chartType={a.chartType} />
    ))),
    item(4, 'Access', 'access', withIssues('access', <Access register={undefined} setValue={undefined} formState={formState} saveAttempted={a.saveAttempted} />)),
    item(5, 'Dashboards', 'dashboards', withIssues('dashboards', <Dashboards register={undefined} setValue={undefined} formState={formState} saveAttempted={a.saveAttempted} />)),
  ];
  // On screen, Dashboards follows Info. The tab ids (and so the error dots and warnings) stay as they are.
  return TAB_DISPLAY_ORDER.map((id) => tabs[id]);
}
